import { capitalize } from 'vue';
import { useInstance } from './useInstance.js';
/**
 * Implementation of the async event emitter composable.
 *
 * This function creates an event emitter that:
 * - Handles both single and multiple event listeners
 * - Supports async listeners and waits for their completion
 * - Returns promises that resolve when all listeners complete
 * - Maintains type safety with Vue's emit system
 *
 * The emitter works by:
 * 1. Converting event names to Vue prop names (e.g., 'send' -> 'onSend')
 * 2. Finding listeners attached to the component's vnode props
 * 3. Calling all listeners and collecting their return values
 * 4. Using Promise.all() to wait for async listeners to complete
 *
 * @returns A function that emits events and returns a Promise
 *
 * @example
 * ```ts
 * // Real-world usage in a chat input component
 * type Events = {
 *   send: [message: string];
 * };
 *
 * const emitAsync = useEmitAsync<Events>();
 *
 * async function handleSend() {
 *   if (message.value?.trim()) {
 *     // This will wait for parent components to handle the send event
 *     // including any async operations like API calls
 *     await emitAsync('send', message.value);
 *
 *     // Only clear the message after all handlers complete
 *     message.value = '';
 *   }
 * }
 * ```
 *
 * @example
 * ```ts
 * // Parent component handling the async event
 * async function handleMessageSend(message: string) {
 *   // This async operation will be awaited by the child component
 *   await api.sendMessage(message);
 *   await updateChatHistory();
 *   showSuccessNotification();
 * }
 * ```
 */
export function useEmitAsync() {
    const instance = useInstance();
    return (event, ...args) => {
        const attrName = 'on' + capitalize(event);
        const listeners = instance.vnode.props?.[attrName];
        if (!listeners) {
            return undefined;
        }
        if (Array.isArray(listeners)) {
            const promises = [];
            for (const listener of listeners) {
                if (typeof listener === 'function') {
                    promises.push(listener(...args));
                }
            }
            // there are many listeners for this event
            return Promise.all(promises);
        }
        else if (typeof listeners === 'function') {
            return listeners(...args);
        }
        return undefined;
    };
}
//# sourceMappingURL=useEmitAsync.js.map